/**
 * IP-based location mapping utility
 *
 * Detects user's approximate location (city, region, coordinates) via backend proxy.
 * The backend extracts the client's real IP from proxy headers and queries ip-api.com.
 *
 * This approach solves mixed-content issues (HTTPS frontend → HTTPS backend → HTTP ip-api.com)
 * and works correctly with Traefik/container networking where backend might see internal IPs.
 *
 * Privacy-friendly: IP address is only used transiently for geolocation lookup.
 * Caching: Location is cached in localStorage for 24 hours to avoid repeated API calls.
 */

export interface UserLocation {
  city: string;
  region: string;
  zip: string;
  lat: number;
  lon: number;
}

interface CachedLocation {
  location: UserLocation;
  timestamp: number;
}

const CACHE_KEY = 'nightingale_mapping'; // v2 = backend proxy
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

// Get API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * Get cached location from localStorage if available and not expired.
 * Exported for use in components that need to bypass React state staleness.
 */
export function getCachedLocation(): UserLocation | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { location, timestamp }: CachedLocation = JSON.parse(cached);
    const age = Date.now() - timestamp;

    if (age > CACHE_DURATION_MS) {
      // Cache expired
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return location;
  } catch (error) {
    console.warn('[IP Location] Failed to read cache:', error);
    return null;
  }
}

/**
 * Save location to localStorage cache.
 */
function cacheLocation(location: UserLocation): void {
  try {
    const cached: CachedLocation = {
      location,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch (error) {
    console.warn('[IP Location] Failed to cache location:', error);
  }
}

/**
 * Get user's approximate location based on their IP address.
 *
 * Calls the backend proxy endpoint which:
 * 1. Extracts the client's real IP from X-Forwarded-For/X-Real-IP headers
 * 2. Queries ip-api.com with that IP
 * 3. Returns location data
 *
 * This approach works correctly with Traefik/container networking and avoids
 * mixed-content blocking issues.
 *
 * Location is cached in localStorage for 24 hours to improve performance and reduce API calls.
 *
 * @returns Location data or null if detection fails
 */
export async function getUserLocation(): Promise<UserLocation | null> {
  // Check cache first
  const cached = getCachedLocation();
  if (cached) {
    return cached;
  }

  try {
    // Call backend proxy endpoint
    const response = await fetch(`${API_BASE_URL}/api/chats/mapping`, {
      method: 'GET',
      signal: AbortSignal.timeout(3000), // 3 second timeout (backend has its own 2s timeout)
    });

    if (!response.ok) {
      console.warn('[IP Location] API request failed:', response.status);
      return null;
    }

    const data = await response.json();

    // Check if the API returned a failure status
    if (data.status !== 'success') {
      console.warn('[IP Location] API returned failure:', data.message);
      return null;
    }

    // Validate we got all required fields
    if (!data.city || !data.region || !data.zip || data.lat === undefined || data.lon === undefined) {
      console.warn('[IP Location] Incomplete location data:', data);
      return null;
    }

    const location: UserLocation = {
      city: data.city,
      region: data.region, // e.g., "Bavaria", "North Rhine-Westphalia"
      zip: data.zip, // Postal code
      lat: data.lat,
      lon: data.lon,
    };

    // Cache the result for future use
    cacheLocation(location);

    return location;

  } catch (error) {
    // Graceful degradation - chat should work even if location mapping fails
    console.warn('[IP Location] Failed to detect location:', error);
    return null;
  }
}
